import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ToastProvider } from "@/context/ToastContext";
import "@/App.css";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import FindPartnersPage from "@/pages/FindPartnersPage";
import ProfilePage from "@/pages/ProfilePage";
import VenuesPage from "@/pages/VenuesPage";
import MessagingPage from "@/pages/MessagingPage";
import PricingPage from "@/pages/PricingPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import SportsDirectoryPage from "@/pages/SportsDirectoryPage";
import FortWorthPage from "@/pages/FortWorthPage"; // kept for backward compat
import AccountPage from "@/pages/AccountPage";
import SessionsPage from "@/pages/SessionsPage";
import ClubsPage from "@/pages/ClubsPage";
import ClubPublicPage from "@/pages/ClubPublicPage";
import ClubDashboardPage from "@/pages/ClubDashboardPage";
import LeaguesPage from "@/pages/LeaguesPage";
import AdminVerificationsPage from "@/pages/AdminVerificationsPage";
import CityPage from "@/pages/CityPage";
import NotFoundPage from "@/pages/NotFoundPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import SafetyCenterPage from "@/pages/SafetyCenterPage";
import CommunityGuidelinesPage from "@/pages/CommunityGuidelinesPage";
import ContactPage from "@/pages/ContactPage";
import OnboardingPage from "@/pages/OnboardingPage";
import PartnersPage from "@/pages/PartnersPage";
import InvitePage from "@/pages/InvitePage";
import ConciergePage from "@/pages/ConciergePage";
import GroupSessionsPage from "@/pages/GroupSessionsPage";
import GroupSessionDetailPage from "@/pages/GroupSessionDetailPage";
import MarketsPage from "@/pages/MarketsPage";
import MarketDetailPage from "@/pages/MarketDetailPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import OrganizerDashboardPage from "@/pages/OrganizerDashboardPage";
import OrganizerSessionDetailPage from "@/pages/OrganizerSessionDetailPage";
import MarketplaceDashboardPage from "@/pages/MarketplaceDashboardPage";
import FacilityPortalPage from "@/pages/FacilityPortalPage";
import FacilityCheckinPage from "@/pages/FacilityCheckinPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * WCAG 2.4.2 Page Titled.
 *
 * This is a single-page app, so without help every route keeps the static
 * <title> from index.html and a screen-reader user tabbing between pages (or a
 * user with several tabs open) has no way to tell them apart. This derives a
 * descriptive title from the current pathname.
 */
const TITLES = {
  "/": "ActivitySports — Find Your Perfect Play Partner",
  "/login": "Sign in — ActivitySports",
  "/register": "Create your account — ActivitySports",
  "/find-partners": "Find sports partners — ActivitySports",
  "/venues": "Sports venues & courts — ActivitySports",
  "/sports": "Browse sports — ActivitySports",
  "/how-it-works": "How ActivitySports works",
  "/pricing": "Pricing & membership — ActivitySports",
  "/messages": "Messages — ActivitySports",
  "/account": "Your account — ActivitySports",
  "/sessions": "Your sessions — ActivitySports",
  "/partners": "Your partners — ActivitySports",
  "/invite": "Invite friends — ActivitySports",
  "/concierge": "AI Concierge — ActivitySports",
  "/group-sessions": "Group sessions — ActivitySports",
  "/markets": "Markets we serve — ActivitySports",
  "/payment/success": "Payment complete — ActivitySports",
  "/payment/cancel": "Payment cancelled — ActivitySports",
  "/organizer": "Organizer dashboard — ActivitySports",
  "/organizer/apply": "Become an organizer — ActivitySports",
  "/facility": "Facility partner portal — ActivitySports",
  "/admin/marketplace": "Marketplace admin — ActivitySports",
  "/admin/verifications": "Verification queue — ActivitySports",
  "/clubs": "Clubs — ActivitySports",
  "/leagues": "Leagues — ActivitySports",
  "/onboarding": "Get started — ActivitySports",
  "/contact": "Contact us — ActivitySports",
  "/safety": "Safety center — ActivitySports",
  "/community": "Community guidelines — ActivitySports",
  "/privacy": "Privacy policy — ActivitySports",
  "/terms": "Terms of service — ActivitySports",
};

function titleFor(pathname) {
  if (TITLES[pathname]) return TITLES[pathname];
  // dynamic segments, e.g. /sports/tennis or /markets/fort-worth
  const seg = pathname.split("/").filter(Boolean);
  if (seg[0] === "sports" && seg[1]) {
    return `${seg[1].replace(/-/g, " ")} partners — ActivitySports`;
  }
  if (seg[0] === "clubs" && seg[1]) return "Club — ActivitySports";
  if (seg[0] === "markets" && seg[1]) {
    return `${seg[1].replace(/-/g, " ")} market — ActivitySports`;
  }
  if (seg[0] === "group-sessions" && seg[1]) return "Group session — ActivitySports";
  if (seg[0] === "facility" && seg[1] === "checkin") return "Venue check-in — ActivitySports";
  if (seg[0] === "organizer" && seg[1] === "sessions") return "Organizer session — ActivitySports";
  if (seg[0] === "profile") return "Player profile — ActivitySports";
  if (seg.length === 3 && seg[0] === "us") {
    return `${seg[2].replace(/-/g, " ")} — ActivitySports`;
  }
  if (seg.length === 0) return TITLES["/"];
  return "Page not found — ActivitySports";
}

/** Local (not exported) - App.js already has its own default export. */
function RouteTitle() {
  const location = useLocation();
  useEffect(() => {
    document.title = titleFor(location.pathname);
  }, [location.pathname]);
  return null;
}

function AppLayout({ children, hideFooter }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <Navbar />
      <main id="main-content" className="flex-1">{children}</main>
      {!hideFooter && <Footer />}
    </div>
  );
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <RouteTitle />
      <Routes>
      <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
      <Route path="/login" element={<AppLayout hideFooter><LoginPage /></AppLayout>} />
      <Route path="/register" element={<AppLayout hideFooter><RegisterPage /></AppLayout>} />
      <Route path="/find-partners" element={<AppLayout><FindPartnersPage /></AppLayout>} />
      <Route path="/profile/:userId" element={<AppLayout><ProfilePage /></AppLayout>} />
      <Route path="/venues" element={<AppLayout><VenuesPage /></AppLayout>} />
      <Route path="/sports" element={<AppLayout><SportsDirectoryPage /></AppLayout>} />
      <Route path="/sports/:sport" element={<AppLayout><SportsDirectoryPage /></AppLayout>} />
      <Route path="/how-it-works" element={<AppLayout><HowItWorksPage /></AppLayout>} />
      <Route path="/pricing" element={<AppLayout><PricingPage /></AppLayout>} />
      <Route path="/messages" element={<ProtectedRoute><AppLayout hideFooter><MessagingPage /></AppLayout></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AppLayout><AccountPage /></AppLayout></ProtectedRoute>} />
      <Route path="/sessions" element={<ProtectedRoute><AppLayout><SessionsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/partners" element={<ProtectedRoute><AppLayout><PartnersPage /></AppLayout></ProtectedRoute>} />
      <Route path="/invite" element={<ProtectedRoute><AppLayout><InvitePage /></AppLayout></ProtectedRoute>} />
      <Route path="/concierge" element={<ProtectedRoute><AppLayout hideFooter><ConciergePage /></AppLayout></ProtectedRoute>} />
      <Route path="/group-sessions" element={<AppLayout><GroupSessionsPage /></AppLayout>} />
      <Route path="/group-sessions/:sessionId" element={<AppLayout><GroupSessionDetailPage /></AppLayout>} />
      <Route path="/markets" element={<AppLayout><MarketsPage /></AppLayout>} />
      <Route path="/markets/:marketId" element={<AppLayout><MarketDetailPage /></AppLayout>} />
      <Route path="/payment/success" element={<AppLayout><PaymentSuccessPage /></AppLayout>} />
      <Route path="/payment/cancel" element={<AppLayout><PaymentCancelPage /></AppLayout>} />
      <Route path="/organizer/apply" element={<ProtectedRoute><AppLayout><OrganizerDashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/organizer" element={<ProtectedRoute><AppLayout><OrganizerDashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/organizer/sessions/:sessionId" element={<ProtectedRoute><AppLayout hideFooter><OrganizerSessionDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/facility" element={<ProtectedRoute><AppLayout hideFooter><FacilityPortalPage /></AppLayout></ProtectedRoute>} />
      <Route path="/facility/checkin/:facilityId" element={<AppLayout hideFooter><FacilityCheckinPage /></AppLayout>} />
      <Route path="/admin/marketplace" element={<ProtectedRoute><AppLayout><MarketplaceDashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/clubs" element={<AppLayout><ClubsPage /></AppLayout>} />
      <Route path="/clubs/:clubId" element={<AppLayout><ClubPublicPage /></AppLayout>} />
      <Route path="/clubs/:clubId/manage" element={<ProtectedRoute><AppLayout><ClubDashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/leagues" element={<AppLayout><LeaguesPage /></AppLayout>} />
      <Route path="/admin/verifications" element={<ProtectedRoute><AppLayout><AdminVerificationsPage /></AppLayout></ProtectedRoute>} />
      {/* Legal Pages */}
      <Route path="/privacy" element={<AppLayout><PrivacyPolicyPage /></AppLayout>} />
      <Route path="/terms" element={<AppLayout><TermsOfServicePage /></AppLayout>} />
      <Route path="/safety" element={<AppLayout><SafetyCenterPage /></AppLayout>} />
      <Route path="/community" element={<AppLayout><CommunityGuidelinesPage /></AppLayout>} />
      <Route path="/contact" element={<AppLayout><ContactPage /></AppLayout>} />
      <Route path="/onboarding" element={<ProtectedRoute><AppLayout hideFooter><OnboardingPage /></AppLayout></ProtectedRoute>} />
      {/* Generic city page — catches all /us/:state/:city */}
      <Route path="/us/:stateSlug/:citySlug" element={<AppLayout><CityPage /></AppLayout>} />
      <Route path="*" element={<AppLayout><NotFoundPage /></AppLayout>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
